import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal, huidigeTaal } from '../i18n.js';
import { MAATREGELEN, DIENSTEN, ACTIES, WAAROM, METINGEN, NIS2, ISO } from '../beveiligingsstatus.js';
import DomeinControle from '../components/DomeinControle.jsx';

/**
 * **Beveiligingsstatus** voor de directie van een klant (docs/ontwerp/beveiligingsstatus.md): de security score, de
 * tien maatregelen uit NIS2 met wat ADTS regelt, hooguit drie acties en de diensten. Gewone taal, geen adressen of
 * alertnummers. De PDF is de printfunctie van de browser (alle maatregelen open).
 *
 * - Medewerkers: voor de gekozen klant, of bij *Alle klanten* een lijst met de score per klant.
 * - De klant zelf en meekijken: `StatusVoorKlant` in Klant.jsx.
 */
const taal = (x) => x?.[huidigeTaal()] ?? x?.nl ?? '';
const vul = (s, w = {}) => s.replace(/\{(\w+)\}/g, (_, k) => (w[k] == null ? '' : String(w[k])));
const van = (lijst, id) => lijst.find((x) => x.id === id);
const OORDEEL = { goed: 'Goed', redelijk: 'Redelijk', aandacht: 'Aandacht nodig' };
const STAND = { geregeld: 'Geregeld door ADTS', deels: 'Deels geregeld', zelf: 'Zelf regelen', extern: 'Buiten ADTS ZERO' };
const kleur = (o) => (o === 'goed' || o === 'geregeld' ? 'goed' : o === 'redelijk' || o === 'deels' ? 'deels' : o === 'extern' ? 'extern' : 'zelf');

const ICOON = {
  goed: <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  deels: <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M8 2a6 6 0 0 1 0 12z" fill="currentColor" /></svg>,
  zelf: <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="5" r="2.6" fill="currentColor" /><path d="M3 14a5 5 0 0 1 10 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>,
};
const Chip = ({ soort, children }) => <span className={`bs-chip ${kleur(soort)}`}>{ICOON[kleur(soort)]}{children}</span>;
const ICOONextern = <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeDasharray="2 2" /></svg>;
ICOON.extern = ICOONextern;

function Ring({ n, oordeel }) {
  const o = 2 * Math.PI * 70;
  return (
    <div className="bs-ring" role="img" aria-label={t('Security score {n} van 100', { n })}>
      <svg viewBox="0 0 160 160" aria-hidden="true"><circle cx="80" cy="80" r="70" fill="none" stroke="var(--line)" strokeWidth="12" />
        <circle cx="80" cy="80" r="70" fill="none" className={`bs-boog ${kleur(oordeel)}`} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${(o * n) / 100} ${o}`} /></svg>
      <div className="bs-cijfer"><span className="n">{n}</span><span className="van">{t('van 100')}</span></div>
    </div>
  );
}

const minuten = (m) => (m == null ? '—' : m < 60 ? t('{n} min', { n: m }) : t('{n} uur', { n: Math.round(m / 6) / 10 }));

export function StatusPagina({ d }) {
  const tel = (s) => d.measures.filter((m) => m.stand === s).length;
  const verschil = d.previous == null ? null : d.score - d.previous;
  const nis = van(NIS2, d.tenant.nis2);
  const meting = (id) => {
    const m = d.measurements[id];
    const tx = van(METINGEN, id);
    return m && tx ? { stand: m.stand, tekst: vul(taal(tx[m.stand]), m) } : null;
  };
  const printen = () => {
    const open = [...document.querySelectorAll('.bs-maatregel')];
    const was = open.map((x) => x.open);
    open.forEach((x) => { x.open = true; });
    window.addEventListener('afterprint', () => open.forEach((x, i) => { x.open = was[i]; }), { once: true });
    window.print();
  };

  return (
    <div className="bs">
      <div className="page-head"><div>
        <h1>{t('Beveiligingsstatus')}</h1>
        <p className="sub">{d.tenant.name} · {new Date(d.at).toLocaleDateString(huidigeTaal() === 'en' ? 'en-GB' : 'nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
          {nis && <> · <span className="bs-nis">NIS2: {taal(nis.tekst)}</span></>}</p>
      </div>
      <div className="actions bs-geen-print"><button type="button" className="btn" onClick={printen}>{t('Download als PDF')}</button></div></div>

      {d.tenant.trialUntil && <div className="callout bs-proef">{t('Proef tot {datum}: wat hieronder staat, is wat ADTS ZERO al ziet. Met ADTS ZERO erbij komt de rest.', { datum: new Date(d.tenant.trialUntil).toLocaleDateString(huidigeTaal() === 'en' ? 'en-GB' : 'nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) })}</div>}

      <section className="card bs-kaart">
        <div className="bs-score">
          <Ring n={d.score} oordeel={d.verdict} />
          <div>
            <div className={`bs-oordeel ${kleur(d.verdict)}`}>{ICOON[kleur(d.verdict)]}{t('Security score: {oordeel}', { oordeel: t(OORDEEL[d.verdict]) })}</div>
            <div className="bs-regels">
              {verschil != null && <div>{t('Vorige maand')} <b>{d.previous}</b>, <span className={verschil >= 0 ? 'bs-op' : 'bs-af'}>{verschil >= 0 ? '▲' : '▼'} {t('{n} punten', { n: Math.abs(verschil) })}</span></div>}
              {d.afterActions > d.score && <div>{t('Met de acties hieronder')}: <b>{d.afterActions}</b></div>}
            </div>
            <details className="bs-hoe">
              <summary>{t('Hoe komt dit cijfer tot stand?')}</summary>
              <p>{t('De score telt alleen de diensten die ADTS ZERO levert, per NIS2-maatregel 0 tot 10 punten, min 3 als een meting aandacht vraagt. Maatregelen zonder ZERO-dienst (continuïteit, leveranciers) tellen niet mee; de score is het gemiddelde over de maatregelen die ADTS ZERO meet. Wat u zelf regelt (beleid, training) telt niet mee: dat ziet ADTS niet.')}{d.domains?.length > 0 && ` ${t('Bij g en h telt het maildomein mee, naar verhouding van zijn score.')}`}</p>
              <div className="bs-per">{d.measures.filter((m) => m.scored).map((m) => (
                <div key={m.id}><div className="bs-staaf"><span className={kleur(m.punten >= 8 ? 'geregeld' : m.punten >= 5 ? 'deels' : 'zelf')} style={{ height: `${m.punten * 10}%` }} /></div>{m.id.toUpperCase()} · {m.punten}</div>
              ))}</div>
            </details>
          </div>
        </div>
        <div className="bs-samen">
          <p className="bs-zin">{t('ADTS ZERO regelt {n} van de {totaal} NIS2-maatregelen die het meet helemaal voor u. De overige maatregelen (zoals continuïteit en leveranciers) regelt u zelf.', { n: tel('geregeld'), totaal: d.measures.filter((m) => m.scored).length })}</p>
          {['geregeld', 'deels', 'zelf'].map((s) => <div key={s} className={`bs-tel ${kleur(s)}`}><span className="n">{tel(s)}</span><Chip soort={s}>{t(STAND[s])}</Chip></div>)}
        </div>
      </section>

      {d.domains?.length > 0 && (
        <section className="card bs-kaart">
          <header><h2>{t('Maildomein en website')}</h2><span className="meta">{t('Uit wat het domein openbaar maakt; er is niets gescand')}</span></header>
          {d.domains.map((x) => <DomeinControle key={x.domain} d={x} />)}
        </section>
      )}

      <section className="card bs-kaart">
        <header><h2>{t('De afgelopen 30 dagen')}</h2></header>
        <div className="bs-getallen">
          <div><div className="n">{getal(d.numbers.tegengehouden)}</div><div className="t">{t('verbindingen van buiten tegengehouden')}</div></div>
          <div><div className="n">{getal(d.numbers.alerts)}</div><div className="t">{t('meldingen bekeken door ADTS')}</div></div>
          <div><div className="n">{getal(d.numbers.ernstig)}</div><div className="t">{t('ernstige meldingen')}</div></div>
          <div><div className="n">{minuten(d.numbers.reactieMinuten)}</div><div className="t">{t('tot ADTS een melding oppakte (mediaan)')}</div></div>
        </div>
      </section>

      <p className="muted bs-stappen-wijs">{t('Wat u nu kunt doen staat apart onder Volgende stappen.')}</p>

      <section className="card bs-kaart">
        <header><h2>{t('De tien maatregelen uit NIS2')}</h2></header>
        <p className="muted">{t('Artikel 21 van NIS2 (in Nederland de Cyberbeveiligingswet), met de bijbehorende ISO 27001-maatregelen ernaast. Klik op een maatregel voor wat NIS2 en ISO 27001 vragen, wat ADTS doet en wat u zelf doet.')}</p>
        {d.measures.map((m) => {
          const tx = van(MAATREGELEN, m.id);
          const me = m.meting && meting(m.meting);
          return (
            <details key={m.id} className="bs-maatregel">
              <summary><span className="letter">{m.id}</span><span className="titel">{taal(tx.titel)}<span className="kort">{taal(tx.kort)}</span>
                {van(ISO, m.id) && <span className="bs-iso-kort">ISO 27001: {van(ISO, m.id).controls.map((c) => `A.${c.nr}`).join(', ')}</span>}</span><Chip soort={m.stand}>{t(STAND[m.stand])}</Chip><span className="pijl" aria-hidden="true">›</span></summary>
              <div className="bs-binnen">
                <div className="bs-wet">{t('NIS2 vraagt')}: {taal(tx.wet)}</div>
                {van(ISO, m.id) && <div className="bs-iso"><b>ISO 27001</b>: {van(ISO, m.id).controls.map((c, i) => <span key={c.nr}>{i > 0 ? ' · ' : ''}A.{c.nr} {taal(c.tekst)}</span>)}</div>}
                <div><h3>{t('Wat ADTS via ADTS ZERO voor u doet')}</h3>
                  {m.diensten.length ? <ul>{m.diensten.map((x) => (
                    <li key={x.id} className={x.afgenomen ? '' : 'niet'}><span>{x.afgenomen ? '✓' : '–'}</span><span>{taal(van(DIENSTEN, x.id)?.naam)}{!x.afgenomen && <em> ({t('niet afgenomen')})</em>}</span></li>
                  ))}</ul> : <p className="muted">{t('Hier levert ADTS ZERO geen dienst voor; dit regelt u zelf.')}</p>}
                {me && <div className="bs-meting"><Chip soort={me.stand === 'goed' ? 'geregeld' : 'deels'}>{me.stand === 'goed' ? t('Nu') : t('Let op')}</Chip> {me.tekst}</div>}
                {m.mail != null && <div className="bs-meting"><Chip soort={m.mail >= 80 ? 'geregeld' : 'deels'}>{m.mail >= 80 ? t('Nu') : t('Let op')}</Chip> {t('Maildomein: {n} van 100 (zie Maildomein en website). Telt hier mee.', { n: m.mail })}</div>}</div>
                <div><h3>{t('Wat u zelf doet')}</h3><ul>{tx.zelf.map((z, i) => <li key={i}><span>•</span><span>{taal(z)}</span></li>)}</ul></div>
              </div>
            </details>
          );
        })}
      </section>

      <section className="card bs-kaart">
        <header><h2>{t('Meldplicht bij een ernstig incident')}</h2></header>
        <p className="muted">{t('Artikel 23. Melden doet u zelf; ADTS levert de tijdlijn: wat er gebeurde, wanneer, en wat er is gedaan.')}</p>
        <div className="bs-meld">
          <div><div className="n">{t('24 uur')}</div>{t('Vroege waarschuwing aan de toezichthouder en het CSIRT')}</div>
          <div><div className="n">{t('72 uur')}</div>{t('Melding met een eerste beoordeling')}</div>
          <div><div className="n">{t('1 maand')}</div>{t('Eindverslag met oorzaak en maatregelen')}</div>
        </div>
      </section>

      <section className="card bs-kaart">
        <header><h2>{t('Uw diensten')}</h2><span className="meta">{t('Gestippeld: telt niet mee. Een module telt pas als hij gegevens heeft.')}</span></header>
        <div className="bs-diensten">{d.services.map((s) => { const x = van(DIENSTEN, s.id); return (
          <div key={s.id} className={`bs-dienst${s.taken ? '' : ' nee'}`}><div className="naam">{taal(x?.naam)}</div><div className="wat">{taal(x?.wat)}</div>
            <div className={`stand ${s.taken ? 'ja' : ''}`}>{s.taken ? `✓ ${t('Afgenomen')}` : s.notSetUp ? t('Aan, nog niet ingericht') : t('Niet afgenomen')} · {x?.groep}</div></div>
        ); })}</div>
      </section>

      <p className="muted bs-voet">{t('Dit overzicht laat zien wat ADTS voor u regelt en wat u zelf moet doen. Het is geen juridisch advies en geen certificering. Onder NIS2 blijft de directie verantwoordelijk voor de maatregelen (artikel 20).')}</p>
    </div>
  );
}

/**
 * **Volgende stappen** (Compliance): de acties uit de beveiligingsstatus, los van het overzicht. Zelfde
 * gegevens (`d.actions`), hooguit drie, de belangrijkste eerst.
 */
export function StappenKaart({ d }) {
  const actie = (a) => {
    const tx = van(ACTIES, a.id);
    if (a.id === 'dienst') {
      const dienst = van(DIENSTEN, a.params.dienst);
      return { wat: vul(taal(tx.wat), { dienst: taal(dienst?.naam) }), waarom: taal(van(WAAROM, a.params.dienst)?.tekst) || taal(dienst?.wat) };
    }
    return { wat: vul(taal(tx.wat), a.params), waarom: taal(tx.waarom) };
  };
  return (
    <div className="bs">
      <div className="page-head"><div>
        <h1>{t('Volgende stappen')}</h1>
        <p className="sub">{d.tenant.name} · {t('De acties die volgen uit de beveiligingsstatus, de belangrijkste eerst.')}</p>
      </div></div>
      {d.afterActions > d.score && <div className="callout bs-stappen-score">{t('Met deze acties gaat de security score van {nu} naar {straks}.', { nu: d.score, straks: d.afterActions })}</div>}
      <section className="card bs-kaart">
        <header><h2>{t('Wat u nu kunt doen')}</h2><span className="meta">{t('De belangrijkste eerst')}</span></header>
        {d.actions.length
          ? <ol className="bs-acties">{d.actions.map((a, i) => { const x = actie(a); return <li key={i}><div className="wat">{x.wat}</div>{x.waarom && <div className="waarom">{x.waarom}</div>}</li>; })}</ol>
          : <p className="muted">{t('Er staan nu geen acties open.')}</p>}
      </section>
    </div>
  );
}

function Laden({ q }) {
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  return <StatusPagina d={q.data.data} />;
}

/** Voor de klant zelf en meekijken. */
export function StatusVoorKlant({ haal, sleutel }) {
  const q = useQuery({ queryKey: [...sleutel, 'status'], queryFn: haal, refetchInterval: 5 * 60_000 });
  return <Laden q={q} />;
}

function EenKlant({ id }) {
  const q = useQuery({ queryKey: ['status', id], queryFn: () => api.status(id), refetchInterval: 5 * 60_000 });
  return <Laden q={q} />;
}

/** Medewerkers: de gekozen klant, of bij Alle klanten de lijst, met de laagste score bovenaan. */
export default function Beveiligingsstatus({ klant }) {
  const [gekozen, kies] = useState(null);
  const lijst = useQuery({ queryKey: ['status', 'klanten'], queryFn: api.statusKlanten, enabled: klant === 'alle' && !gekozen });
  if (klant !== 'alle') return <EenKlant id={klant} />;
  if (gekozen) return <><button type="button" className="btn bs-geen-print bs-terug" onClick={() => kies(null)}>← {t('Alle klanten')}</button><EenKlant id={gekozen} /></>;
  return (
    <>
      <div className="page-head"><div><h1>{t('Beveiligingsstatus')}</h1>
        <p className="sub">{t('De security score per klant, de laagste bovenaan. Kies een klant voor het overzicht voor de directie.')}</p></div></div>
      {lijst.isPending ? <p className="muted">{t('Laden…')}</p> : lijst.isError ? <div className="melder rood">{t(lijst.error.message)}</div> : (
        <section className="card">
          <table>
            <thead><tr><th>{t('Klant')}</th><th>{t('Score')}</th><th className="hide-sm">{t('Vorige maand')}</th><th className="hide-sm">NIS2</th>
              <th className="hide-sm">{t('Geregeld · deels · zelf')}</th></tr></thead>
            <tbody>{lijst.data.data.map((r) => (
              <tr key={r.tenant.id} className="bs-rij" onClick={() => kies(r.tenant.id)}>
                <td><button type="button" className="bs-link" onClick={() => kies(r.tenant.id)}>{r.tenant.name}</button></td>
                <td><Chip soort={r.verdict}>{r.score}</Chip></td>
                <td className="hide-sm">{r.previous ?? '—'}</td>
                <td className="hide-sm">{taal(van(NIS2, r.tenant.nis2)?.tekst) || '—'}</td>
                <td className="hide-sm">{r.counts.join(' · ')}</td>
              </tr>
            ))}</tbody>
          </table>
        </section>
      )}
    </>
  );
}
